import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from '../../common/Toast/toast';
import PopupDone, { usePopupDone } from "../../common/PopupDone/PopupDone.jsx";
import { Users } from "lucide-react";
import { deleteUser, getUsers } from "../../../../services/UserSerivce.js";
import { getappstoroles } from "../../../../services/AppToRole.js";
import { useAuth } from "../../../../context/AuthContext";
import NewCustomDataTable from "../../common/NewCustomDataTable.jsx";
import SettingsFormModal from "../../common/SettingsFormModal.jsx";
import AddUser from "./AddUser.jsx";
import useScrollLoadMore from "../../common/useScrollLoadMore";
import { initialsOf, avatarTone } from "../../Trainer/Attendance/AttendanceBits.jsx";
// Built to the same plan as /list-attendance, so it reads that page's
// single stylesheet rather than keeping one of its own.
import "../../Trainer/TrainerDashboard.css";
import FilterSheet from "../../common/FilterSheet/FilterSheet";
import useDebouncedCallback from "../../common/useDebouncedCallback";
import { confirm } from '../../common/Confirm/confirm';

const DASH = "—";

// One call when the whole directory has to be held in memory to filter it.
const ALL_USERS_LIMIT = 20000;

const isMongoObjectId = (id) => /^[a-f\d]{24}$/i.test(String(id || "").trim());

const capitalizeWords = (str) => {
  if (!str) return "";
  return String(str)
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const dateOf = (value) => {
  if (!value) return DASH;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? DASH
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
};

/* ── The record popup ────────────────────────────────────────────────
   Same plan as the attendance list's popup: title and a pgm-ident row of
   labelled facts in the header, pgm-section blocks in the body each led
   by a pgm-section-title, pgm-table for anything with more than one
   value, pgm-empty when there is nothing, and a footer whose actions sit
   left of a filled Close.
   ------------------------------------------------------------------ */
const UserDetailModal = ({ detail, onClose, onEdit, onDelete }) => {
  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (e) => {
      // A form opened from here sits on top; Escape is its, not this popup's.
      if (e.key === "Escape" && !document.querySelector(".pg-modal--over")) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [detail, onClose]);

  // A delete from here swaps the record for the done panel, which closes
  // the popup five seconds later.
  const { done } = usePopupDone(Boolean(detail));

  if (!detail) return null;

  const {
    name,
    firstName,
    lastName,
    email,
    mobileNumber,
    statusLabel,
    isActive,
    createdAt,
    updatedAt,
    applications,
    roleGroups,
    canDelete,
  } = detail;

  const account = [
    { label: "First name", value: firstName || DASH },
    { label: "Last name", value: lastName || DASH },
    { label: "Email", value: email || DASH },
    { label: "Contact number", value: mobileNumber || DASH },
    { label: "Created", value: dateOf(createdAt) },
    { label: "Last updated", value: dateOf(updatedAt) },
  ];

  return (
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="usr-detail-title"
      onClick={onClose}
    >
      <div
        className="pg-modal-panel pg-modal-panel--wide"
        onClick={(e) => e.stopPropagation()}
      >
        {/* The person is what the record is about, so they are the title. */}
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="usr-detail-title">
              {name}
            </h2>
            <dl className="pgm-ident">
              <div className="pgm-ident-row">
                <dt>Email</dt>
                <dd>{email || DASH}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Contact</dt>
                <dd>{mobileNumber || DASH}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Status</dt>
                <dd>{statusLabel}</dd>
              </div>
            </dl>
          </div>

          <button
            type="button"
            className="pg-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {done ? (
            <PopupDone message={done} onClose={onClose} />
          ) : (
            <>
              <section className="pgm-section">
                <h3 className="pgm-section-title">Account</h3>
                <table className="pgm-table">
                  <thead>
                    <tr>
                      <th className="pgm-num">#</th>
                      <th>Detail</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {account.map((f, i) => (
                      <tr key={f.label}>
                        <td className="pgm-num">{i + 1}</td>
                        <td>{f.label}</td>
                        <td>{f.value}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="pgm-num">{account.length + 1}</td>
                      <td>Status</td>
                      <td>
                        <span
                          className={`pgm-pill ${
                            isActive ? "pgm-pill--good" : "pgm-pill--bad"
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">
                  Applications
                  <span className="pgm-section-count">{applications.length}</span>
                </h3>

                {applications.length === 0 ? (
                  <p className="pgm-empty">
                    This account is not assigned to any application.
                  </p>
                ) : (
                  <table className="pgm-table">
                    <thead>
                      <tr>
                        <th className="pgm-num">#</th>
                        <th>Application</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((app, i) => (
                        <tr key={app}>
                          <td className="pgm-num">{i + 1}</td>
                          <td>{app}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              {/* This used to be an HTML string built with <strong> tags and
                  handed to dangerouslySetInnerHTML inside a table cell. It is
                  a table now, which is what it always was. */}
              <section className="pgm-section">
                <h3 className="pgm-section-title">
                  Roles by application
                  <span className="pgm-section-count">{roleGroups.length}</span>
                </h3>

                {roleGroups.length === 0 ? (
                  <p className="pgm-empty">No roles are assigned to this account.</p>
                ) : (
                  <table className="pgm-table">
                    <thead>
                      <tr>
                        <th className="pgm-num">#</th>
                        <th>Application</th>
                        <th>Roles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roleGroups.map((group, i) => (
                        <tr key={group.app}>
                          <td className="pgm-num">{i + 1}</td>
                          <td>{group.app}</td>
                          <td>{group.roles.join(", ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </>
          )}
        </div>

        {/* What you can do with the record sits with Close, at the end.
            Close stays last and is the only filled button, so the
            destructive one is never the default under the cursor.

            Your own account gets no Delete button rather than a greyed
            one -- a control that cannot act does not belong. */}
        {/* The done panel replaces the actions once a delete lands. */}
        {!done && (
          <footer className="pg-modal-foot">
            <div className="pgm-actions">
              <button type="button" className="pgm-btn" onClick={onEdit}>
                Edit
              </button>
              {canDelete && (
                <button
                  type="button"
                  className="pgm-btn pgm-btn--danger"
                  onClick={onDelete}
                >
                  Delete
                </button>
              )}
            </div>
            <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
              Close
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

const User = () => {
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // 25 a batch. The list scrolls rather than paging, so this is how much
  // arrives per fetch, not how much the reader has to click through.
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  // Fetching the next scroll batch. Kept apart from `loading` because that
  // one draws a veil over the whole table -- right for a fresh load, wrong
  // for adding rows to the bottom of a list being read.
  const [loadingMore, setLoadingMore] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  /* The add/edit dialog. null is closed; {} is a new record; { recordId }
     edits that one. The form is the same component the route renders --
     it just reports back here instead of navigating. */
  const [formModal, setFormModal] = useState(null);
  /* Which step the form is on, and its question -- the dialog head asks
     it. Cleared with the dialog, so the next open starts on step one's
     copy rather than flashing the step the last one ended on. */
  const [formStep, setFormStep] = useState(null);

  // Closing the dialog and forgetting the step it was on are the same act,
  // so nothing can do one without the other.
  const closeForm = useCallback(() => {
    setFormModal(null);
    setFormStep(null);
  }, []);
  const [applicationsList, setApplicationsList] = useState(["All"]);

  // What is typed into the filter section but not yet applied.
  const [searchDraft, setSearchDraft] = useState("");
  const [searchFieldDraft, setSearchFieldDraft] = useState("email");
  const [appDraft, setAppDraft] = useState("all");
  const [statusDraft, setStatusDraft] = useState("all");

  // What is actually in force.
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("email");
  const [appFilter, setAppFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const rolesMapRef = useRef({});
  const allUsersRef = useRef([]);
  const isFetching = useRef(false);
  const appendRef = useRef(false);
  const listRef = useRef(null);
  // The foot under the list. It is what the observer watches, and it
  // sits in the normal flow of the page -- no pane of its own.
  const listEndRef = useRef(null);
  const { user: currentUser } = useAuth();

  // ─── Roles ────────────────────────────────────────────────────────────

  /* Every app-to-role mapping, not just the first page -- a role that
     falls past the page cap never makes it into rolesMap, which silently
     breaks both the application filter (that app never appears as an
     option) and the roles column for every user carrying that role. */
  const fetchRoles = useCallback(async () => {
    try {
      setRolesLoading(true);
      const rolesMapData = {};
      const appsSet = new Set();

      const pageSize = 100;
      let page = 1;
      let total = Infinity;
      let fetchedCount = 0;

      while (fetchedCount < total) {
        const response = await getappstoroles(pageSize, page);
        const batch = Array.isArray(response.data) ? response.data : [];
        total = Number(response.pagination?.total ?? fetchedCount + batch.length);
        if (batch.length === 0) break;

        batch.forEach((role) => {
          const applicationName = role.appstorole?.[0]?.app?.label || "Other";
          appsSet.add(applicationName);

          rolesMapData[role.id] = {
            name: role.label || role.name || `Role ${role.id}`,
            application: applicationName,
          };

          if (role.value && role.value !== role.id) {
            rolesMapData[role.value] = {
              name: role.label || role.name || `Role ${role.value}`,
              application: applicationName,
            };
          }

          if (role.profileIds) {
            const profileIdsArray = Array.isArray(role.profileIds)
              ? role.profileIds
              : role.profileIds.split(",");
            profileIdsArray.forEach((profileId) => {
              if (profileId.trim()) {
                rolesMapData[profileId.trim()] = {
                  name: role.label || role.name || `Role ${profileId}`,
                  application: applicationName,
                };
              }
            });
          }
        });

        fetchedCount += batch.length;
        page += 1;
        if (batch.length < pageSize) break; // short page -- no more left
      }

      rolesMapRef.current = rolesMapData;
      setApplicationsList(["All", ...Array.from(appsSet).sort()]);
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // ─── Shaping a user ───────────────────────────────────────────────────

  /* Roles grouped under the application they belong to. Returned as data
     rather than as a string of markup, so the popup can lay it out as a
     table and the list can count it. */
  const roleGroupsOf = (profileIds) => {
    const map = rolesMapRef.current;
    if (!Array.isArray(profileIds) || profileIds.length === 0) return [];

    const byApp = {};
    profileIds.forEach((roleId) => {
      const role = map[roleId];
      if (role) {
        const app = role.application || "Other";
        if (!byApp[app]) byApp[app] = [];
        byApp[app].push(role.name);
      } else if (!isMongoObjectId(roleId)) {
        if (!byApp.Other) byApp.Other = [];
        byApp.Other.push(capitalizeWords(roleId));
      }
    });

    return Object.entries(byApp).map(([app, roles]) => ({
      app: capitalizeWords(app),
      roles,
    }));
  };

  const applicationsOf = (user) => {
    const map = rolesMapRef.current;
    if (Array.isArray(user.applications) && user.applications.length > 0) {
      return user.applications.map(capitalizeWords);
    }
    if (Array.isArray(user.profileIds) && user.profileIds.length > 0) {
      const apps = new Set();
      user.profileIds.forEach((roleId) => {
        const role = map[roleId];
        if (role?.application) apps.add(role.application);
      });
      return Array.from(apps).map(capitalizeWords);
    }
    return [];
  };

  const transformUser = (user) => {
    const profileIds = user.profileIds
      ? Array.isArray(user.profileIds)
        ? user.profileIds
        : user.profileIds.split(",")
      : [];
    const base = { ...user, profileIds };
    const apps = applicationsOf(base);
    const groups = roleGroupsOf(profileIds);
    return {
      ...base,
      applicationList: apps,
      applications: apps.join(", "),
      roleGroups: groups,
      // Kept flat for the client-side search, which matches on text.
      role: groups.map((g) => `${g.app}: ${g.roles.join(", ")}`).join("\n"),
    };
  };

  // ─── Which mode the list is in ────────────────────────────────────────

  /* "role" and "applications" are computed here from profileIds and the
     roles map -- they do not exist as fields on the user document, so the
     server cannot search them. Neither can it filter by application or by
     status. Any of those turns the whole directory into a local list. */
  const usesClientFilter =
    appFilter !== "all" ||
    statusFilter !== "all" ||
    searchField === "role" ||
    searchField === "applications";

  const fetchAllUsers = async () => {
    if (allUsersRef.current.length > 0) return allUsersRef.current;
    const response = await getUsers(ALL_USERS_LIMIT, 1, "", "");
    const list = (response.data || []).map(transformUser);
    allUsersRef.current = list;
    return list;
  };

  const filterLocally = (list) => {
    let out = list;

    if (appFilter !== "all") {
      out = out.filter((user) =>
        (user.applicationList || []).some(
          (app) => app.toLowerCase() === appFilter.toLowerCase(),
        ),
      );
    }

    if (statusFilter !== "all") {
      out = out.filter(
        (user) => String(user.status || "").toLowerCase() === statusFilter,
      );
    }

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      const has = (v) => String(v || "").toLowerCase().includes(term);
      out = out.filter((user) => {
        if (searchField === "email") return has(user.email);
        if (searchField === "firstName") return has(user.firstName);
        if (searchField === "lastName") return has(user.lastName);
        if (searchField === "mobileNumber") return has(user.mobileNumber);
        if (searchField === "status") return has(user.status);
        if (searchField === "applications") return has(user.applications);
        if (searchField === "role") return has(user.role);
        return (
          has(user.email) ||
          has(user.firstName) ||
          has(user.lastName) ||
          has(user.mobileNumber) ||
          has(user.status) ||
          has(user.applications) ||
          has(user.role)
        );
      });
    }

    return out;
  };

  // ─── Load ─────────────────────────────────────────────────────────────

  /* One entry point for both modes. In server mode currentPage is the
     page to fetch; in local mode it is how many batches of the filtered
     list to show. Either way the reader sees the same growing list. */
  const load = useCallback(async () => {
    if (isFetching.current) return;
    isFetching.current = true;
    const isAppend = appendRef.current;
    if (isAppend) setLoadingMore(true);
    else setLoading(true);

    try {
      if (usesClientFilter) {
        const all = await fetchAllUsers();
        const filtered = filterLocally(all);
        setTotalRecords(filtered.length);
        setRows(filtered.slice(0, currentPage * recordsPerPage));
      } else {
        const field = searchField !== "all" ? searchField : "";
        const response = await getUsers(
          recordsPerPage,
          currentPage,
          searchTerm,
          field,
        );
        const pageData = (response.data || []).map(transformUser);
        setTotalRecords(Number(response.pagination?.total || 0));

        // Guarded by id so a repeated page cannot double up a row.
        if (isAppend) {
          setRows((prev) => {
            const seen = new Set(prev.map((r) => r.id));
            return [...prev, ...pageData.filter((r) => !seen.has(r.id))];
          });
        } else {
          setRows(pageData);
        }
      }
      appendRef.current = false;
    } catch (error) {
      console.error("Error fetching users:", error);
      if (!isAppend) {
        setRows([]);
        setTotalRecords(0);
      }
      appendRef.current = false;
      toast.error("Failed to fetch users. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetching.current = false;
    }
    // filterLocally and fetchAllUsers read the same applied values this
    // list already depends on, and the roles map is a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    recordsPerPage,
    searchTerm,
    searchField,
    appFilter,
    statusFilter,
    usesClientFilter,
  ]);

  useEffect(() => {
    // The roles map has to exist first: it is what turns a profile id into
    // an application name, and both the filter and the columns need it.
    if (!rolesLoading) load();
  }, [load, rolesLoading]);

  // ─── Scroll ───────────────────────────────────────────────────────────

  // The scroll asked for the next batch. Only ever moves forward, and only
  // while there is more than is already on screen.
  const loadMore = useCallback(() => {
    if (isFetching.current || loading || loadingMore) return;
    if (rows.length >= totalRecords) return;
    appendRef.current = true;
    setCurrentPage((p) => p + 1);
  }, [loading, loadingMore, rows.length, totalRecords]);

  // The page is the scroller, so the foot of the list coming into view is
  // the signal for the next batch -- see useScrollLoadMore.
  useScrollLoadMore(listEndRef, loadMore, [loadMore, rows.length]);

  // ─── Handlers ─────────────────────────────────────────────────────────

  /* The search box does not wait for Apply. It runs itself once the
     typing stops -- the pickers beside it still wait, because
     choosing three of those should cost one refetch, not three. */
  const runSearch = useDebouncedCallback((term, field) => {
    appendRef.current = false;
    setSearchTerm(term.trim());
    setSearchField(field);
    setCurrentPage(1);
  });

  const handleApplyFilters = () => {
    /* This sets the search itself, so a keystroke still waiting out
       its delay must not land on top of it a moment later. */
    runSearch.cancel();
    appendRef.current = false;
    setSearchTerm(searchDraft.trim());
    setSearchField(searchFieldDraft);
    setAppFilter(appDraft);
    setStatusFilter(statusDraft);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    /* This sets the search itself, so a keystroke still waiting out
       its delay must not land on top of it a moment later. */
    runSearch.cancel();
    appendRef.current = false;
    setSearchDraft("");
    setSearchFieldDraft("email");
    setAppDraft("all");
    setStatusDraft("all");
    setSearchTerm("");
    setSearchField("email");
    setAppFilter("all");
    setStatusFilter("all");
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (newLimit) => {
    appendRef.current = false;
    setRecordsPerPage(Number(newLimit));
    setCurrentPage(1);
  };

  const hasActiveFilters =
    Boolean(searchTerm) ||
    searchField !== "email" ||
    appFilter !== "all" ||
    statusFilter !== "all";

  const handleDelete = async (id) => {
    if (currentUser && currentUser.id === id) {
      toast.warning("You cannot delete your own account.");
      return;
    }

    const result = await confirm({
      title: "Delete Confirmation",
      tone: 'danger',
      text: "Are you sure you want to delete this user?",
      confirmButtonText: "Yes, Delete it!",
      cancelButtonText: "No, Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteUser(id);
      // Gone: the popup that showed it swaps to the done panel and closes
      // itself five seconds later.
      // The cached directory is now stale in both modes.
      allUsersRef.current = [];
      appendRef.current = false;
      if (currentPage === 1) load();
      else setCurrentPage(1);
      toast.success("The user has been deleted successfully.");
    } catch (error) {
      console.error("Delete user error:", error);
      toast.error("Failed to delete the user. Please try again.");
    }
  };

  // ─── Opening a record ─────────────────────────────────────────────────

  const openRecord = (row) => {
    if (!row) return;
    const status = String(row.status || "").toLowerCase();
    const name = `${row.firstName || ""} ${row.lastName || ""}`.trim();

    setDetail({
      id: row.id,
      name: name || row.email || "User",
      firstName: row.firstName,
      lastName: row.lastName,
      email: row.email,
      mobileNumber: row.mobileNumber,
      statusLabel: status ? capitalizeWords(status) : DASH,
      isActive: status === "active",
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
      applications: row.applicationList || [],
      roleGroups: row.roleGroups || [],
      canDelete: Boolean(currentUser && currentUser.id !== row.id),
    });
  };

  // ─── Headers ──────────────────────────────────────────────────────────

  /* Five columns and no more. Sr No, the roles by application, both
     timestamps and the row of action buttons are all in the popup a row
     opens -- the same trade the attendance list makes. */
  const headers = [
    {
      title: "User",
      prop: "firstName",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) => {
        const name = `${row.firstName || ""} ${row.lastName || ""}`.trim();
        return (
          <div className="td-person">
            <span
              className={`td-person-avatar td-person-avatar--${avatarTone(name)}`}
              aria-hidden="true"
            >
              {initialsOf(name)}
            </span>
            <span className="td-person-id">
              <b className="td-person-name">{name || DASH}</b>
            </span>
          </div>
        );
      },
    },
    {
      title: "Email",
      prop: "email",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.email ? <span>{row.email}</span> : <span className="pg-none">{DASH}</span>,
    },
    {
      title: "Contact Number",
      prop: "mobileNumber",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.mobileNumber ? (
          <span className="td-person-tel">{row.mobileNumber}</span>
        ) : (
          <span className="pg-none">{DASH}</span>
        ),
    },
    {
      title: "Applications",
      prop: "applications",
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.applications ? (
          <span className="pg-course" title={row.applications}>
            {row.applications}
          </span>
        ) : (
          <span className="pg-none">{DASH}</span>
        ),
    },
    {
      title: "Status",
      prop: "status",
      filterable: true,
      sortable: true,
      cell: (row) => {
        const status = String(row.status || "").toLowerCase();
        if (!status) return <span className="pg-none">{DASH}</span>;
        return (
          <span
            className={`pg-pill ${
              status === "active" ? "pg-pill--good" : "pg-pill--bad"
            }`}
          >
            {capitalizeWords(status)}
          </span>
        );
      },
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="pg">

      <SettingsFormModal
        open={Boolean(formModal)}
        /* The form is two steps and reports which one it is on, so the
           dialog asks the step's question rather than repeating a fixed
           title over changing content. The fallbacks are what shows for
           the frame before the first report arrives. */
        eyebrow={formModal?.recordId ? "Edit user" : "New user"}
        title={formStep?.title || "Who is this person?"}
        subtitle={
          formStep?.hint || "Their name, how to reach them, and how they sign in."
        }
        labelledBy="user-form-title"
        size="wide"
        onClose={closeForm}
      >
        <AddUser
          embedded
          recordId={formModal?.recordId}
          onStepChange={(copy) => setFormStep(copy)}
          onCancel={closeForm}
          onDone={() => {
            // Saved: the dialog swaps the form for the done panel and closes
            // itself five seconds later (SettingsFormModal).
            // The record changed, so the view popup still open behind this form
            // would be showing the old one. It goes; the form shows the done panel.
            setDetail(null);
            // Rebuild from page 1 so a new record appears and an edited one
            // shows its new values.
            appendRef.current = false;
            if (currentPage === 1) load();
            else setCurrentPage(1);
          }}
        />
      </SettingsFormModal>

      <UserDetailModal
        detail={detail}
        onClose={() => setDetail(null)}
        onEdit={() => {
          const id = detail?.id;
          if (id) setFormModal({ recordId: String(id) });
        }}
        onDelete={() => {
          const id = detail?.id;
          if (id) handleDelete(id);
        }}
      />

      {/* Every control that narrows the list lives here, the search box
          included -- the table's own toolbar is switched off below so the
          two are not stacked one above the other. Nothing here takes
          effect until Apply, so picking three filters is one refetch. */}
      <FilterSheet label="Filter users">
        {/* Row one: the search box, the field it searches, and how many
            rows a load brings. */}
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="usr-search">
              Search
            </label>
            <input
              id="usr-search"
              type="text"
              className="pg-input pg-input--search"
              placeholder="Search by name, email, contact, application or role..."
              value={searchDraft}
              onChange={(e) => {
                setSearchDraft(e.target.value);
                runSearch(e.target.value, searchFieldDraft);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApplyFilters();
              }}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="usr-search-field">
              Search in
            </label>
            <select
              id="usr-search-field"
              className="pg-select"
              value={searchFieldDraft}
              onChange={(e) => {
                setSearchFieldDraft(e.target.value);
                if (searchDraft.trim()) runSearch.flush(searchDraft, e.target.value);
              }}
            >
              <option value="all">All fields</option>
              <option value="email">Email</option>
              <option value="firstName">First name</option>
              <option value="lastName">Last name</option>
              <option value="mobileNumber">Contact number</option>
              <option value="status">Status</option>
              <option value="applications">Applications</option>
              <option value="role">Roles</option>
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="usr-rows">
              Rows per load
            </label>
            <select
              id="usr-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(e.target.value)}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Row two: the pickers, then the buttons. */}
        <div className="pg-filters-row">
          <div className="pg-filters-fields pg-filters-fields--notrainer">
            <div className="pg-field">
              <label className="pg-label" htmlFor="usr-app">
                Application
              </label>
              <select
                id="usr-app"
                className="pg-select"
                value={appDraft}
                onChange={(e) => setAppDraft(e.target.value)}
              >
                {applicationsList.map((app) => (
                  <option key={app} value={app.toLowerCase()}>
                    {app === "All" ? "All applications" : app}
                  </option>
                ))}
              </select>
            </div>

            <div className="pg-field">
              <label className="pg-label" htmlFor="usr-status">
                Status
              </label>
              <select
                id="usr-status"
                className="pg-select"
                value={statusDraft}
                onChange={(e) => setStatusDraft(e.target.value)}
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="pg-filters-actions">
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={handleApplyFilters}
            >
              Apply
            </button>
            <button
              type="button"
              className="pg-btn"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
            >
              Clear
            </button>

            <span className="pg-filters-sep" aria-hidden="true" />

            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => setFormModal({})}
            >
              Add user
            </button>
          </div>
        </div>
      </FilterSheet>

      {/* --userlist: the rules that give a row its pointer and hover are
          unscoped, so the name has to be its own and unmistakable. */}
      <div className="td-list td-list--userlist" ref={listRef}>
        <NewCustomDataTable
          data={rows}
          headers={headers}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => openRecord(row)}
          // Every cell wraps its text in a span, so the default
          // ['TR', 'TD'] would leave the text itself unclickable and only
          // the padding around it would open the record.
          validRowClickTagNames="*"
          rowProps={(row) => ({
            tabIndex: 0,
            role: "button",
            "aria-label": `Open ${
              `${row.firstName || ""} ${row.lastName || ""}`.trim() ||
              row.email ||
              "this user"
            }`,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openRecord(row);
              }
            },
          })}
        />
      </div>

      {/* What the page buttons used to say, said once: how far down the
          list the reader is. Scrolling near the bottom fetches the next
          batch. */}
      <div className="pg-scroll-foot" ref={listEndRef}>
        {rows.length < totalRecords ? (
          <>
            <span className="pg-scroll-count">
              {rows.length} of {totalRecords}
            </span>
            {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
          </>
        ) : (
          totalRecords > 0 && (
            <span className="pg-scroll-count">
              All {totalRecords} user{totalRecords === 1 ? "" : "s"} loaded
            </span>
          )
        )}
      </div>

      {!loading && !rolesLoading && rows.length === 0 && (
        <div className="pg-state">
          <span className="pg-state-icon">
            <Users size={26} aria-hidden="true" />
          </span>
          <h3 className="pg-state-title">No users found</h3>
          <p className="pg-state-text">
            {hasActiveFilters
              ? "Nothing matches the filters in force."
              : "No user account exists yet."}
          </p>
        </div>
      )}
    </div>
  );
};

export default User;
